import { create } from 'zustand';
import { detectPlatform, isStandalone, markInstalled, UPDATE_READY_EVENT, type InstallPlatform } from './install';

/**
 * The browser's `beforeinstallprompt` event, which the DOM typings do not
 * declare because only Chromium fires it. Kept and called later, it opens the
 * browser's own install dialog from a button of ours.
 */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallState {
  /** The deferred prompt, when the browser has offered one and it has not been used. */
  deferredPrompt: BeforeInstallPromptEvent | null;
  /** Running as the installed app, or `appinstalled` fired in this session. */
  installed: boolean;
  /** Whether this page is the installed app (as opposed to a tab on a device that also has it). */
  standalone: boolean;
  platform: InstallPlatform;
  /** A new build's service worker has installed behind the running one; a reload switches to it. */
  updateReady: boolean;
}

/**
 * One store rather than state in the hook, for a reason of timing.
 *
 * `beforeinstallprompt` fires when the browser has checked the manifest and
 * the service worker, which on a fast load is BEFORE React has rendered the
 * Settings page - and an event nobody was listening for is gone. So
 * `listenForInstallSignals` runs from main.tsx before the first render and
 * parks the event here, where any component can find it later. The same
 * holds for the update event: a build that installed while Settings was not
 * mounted still has to be announced.
 */
export const useInstallStore = create<InstallState>()(() => ({
  deferredPrompt: null,
  installed: false,
  standalone: false,
  platform: 'desktop',
  updateReady: false,
}));

/**
 * Wire the browser's signals into the store. Called once, before render.
 * Idempotent through `listening`, because StrictMode is not the only thing
 * that can call a setup twice.
 */
let listening = false;
export function listenForInstallSignals(win: Window = window): void {
  if (listening) return;
  listening = true;

  const nav = win.navigator as Navigator & { standalone?: boolean };
  const standalone = isStandalone(win.matchMedia?.bind(win), nav);
  useInstallStore.setState({
    standalone,
    installed: standalone,
    platform: detectPlatform(nav.userAgent, nav.maxTouchPoints),
  });

  win.addEventListener('beforeinstallprompt', (event) => {
    // Chrome would otherwise show its own mini-infobar at a moment of its
    // choosing. Held back, the prompt is ours to open from the button.
    event.preventDefault();
    useInstallStore.setState({ deferredPrompt: event as BeforeInstallPromptEvent });
  });

  win.addEventListener('appinstalled', () => {
    try {
      markInstalled(win.localStorage);
    } catch {
      // No storage; the session still knows.
    }
    useInstallStore.setState({ deferredPrompt: null, installed: true });
  });

  win.addEventListener(UPDATE_READY_EVENT, () => {
    useInstallStore.setState({ updateReady: true });
  });
}

/**
 * Open the browser's install dialog. Resolves to what the person chose; a
 * prompt can be used once, so it is dropped either way and the browser will
 * offer a fresh one if it wants to.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const { deferredPrompt } = useInstallStore.getState();
  if (!deferredPrompt) return 'unavailable';
  useInstallStore.setState({ deferredPrompt: null });
  await deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  if (outcome === 'accepted') useInstallStore.setState({ installed: true });
  return outcome;
}
